/**
 * Application Configuration
 * Centralized configuration management for secure environment variable handling
 * Similar to Flutter's AppConfig implementation
 */

export class AppConfig {
  // API Configuration
  static readonly baseUrl: string =
    import.meta.env.VITE_API_BASE_URL || "https://ftx-pay.futurix.xyz";
  static readonly applicationId: string =
    import.meta.env.VITE_APPLICATION_ID ||
    "03a895c0-bb96-406c-9924-08087fe1f34a";

  // Firebase Configuration
  static readonly firebaseApiKey: string =
    import.meta.env.VITE_FIREBASE_API_KEY || "";
  static readonly firebaseAuthDomain: string =
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "";
  static readonly firebaseProjectId: string =
    import.meta.env.VITE_FIREBASE_PROJECT_ID || "";
  static readonly firebaseStorageBucket: string =
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "";
  static readonly firebaseMessagingSenderId: string =
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "";
  static readonly firebaseAppId: string =
    import.meta.env.VITE_FIREBASE_APP_ID || "";
  static readonly firebaseMeasurementId: string =
    import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "";

  // Alert Service Configuration
  static readonly alertWebhookUrl: string =
    import.meta.env.VITE_ALERT_WEBHOOK_URL || "";

  // Environment Detection
  static get isDebugMode(): boolean {
    return import.meta.env.DEV;
  }

  static get isProduction(): boolean {
    return import.meta.env.PROD;
  }

  /**
   * Validate API configuration
   * Ensures critical configuration values are present
   */
  static validateApiConfiguration(): void {
    const errors: string[] = [];

    if (!this.baseUrl) {
      errors.push("Missing VITE_API_BASE_URL");
    }

    if (!this.applicationId) {
      errors.push("Missing VITE_APPLICATION_ID");
    }

    if (errors.length > 0) {
      const errorMessage = `Configuration Error:\n${errors.join("\n")}`;
      this.log(errorMessage, "error");
      throw new Error(errorMessage);
    }

    this.log("API configuration validated successfully", "info");
  }

  /**
   * Validate Firebase configuration
   */
  static validateFirebaseConfiguration(): void {
    const errors: string[] = [];

    if (!this.firebaseApiKey) errors.push("Missing VITE_FIREBASE_API_KEY");
    if (!this.firebaseAuthDomain)
      errors.push("Missing VITE_FIREBASE_AUTH_DOMAIN");
    if (!this.firebaseProjectId)
      errors.push("Missing VITE_FIREBASE_PROJECT_ID");
    if (!this.firebaseAppId) errors.push("Missing VITE_FIREBASE_APP_ID");

    if (errors.length > 0) {
      const errorMessage = `Firebase Configuration Error:\n${errors.join("\n")}`;
      this.log(errorMessage, "error");
      throw new Error(errorMessage);
    }

    this.log("Firebase configuration validated successfully", "info");
  }

  /**
   * Logging utility with environment awareness
   */
  static log(
    message: string,
    level: "info" | "warn" | "error" = "info"
  ): void {
    if (this.isDebugMode || level === "error") {
      const prefix = this.isDebugMode ? "[DEBUG]" : "[PROD]";
      const timestamp = new Date().toISOString();

      switch (level) {
        case "error":
          console.error(`${prefix} [${timestamp}] ERROR:`, message);
          break;
        case "warn":
          console.warn(`${prefix} [${timestamp}] WARN:`, message);
          break;
        default:
          console.debug(`${prefix} [${timestamp}] INFO:`, message);
      }
    }
  }

  /**
   * Get Firebase configuration object
   */
  static getFirebaseConfig() {
    return {
      apiKey: this.firebaseApiKey,
      authDomain: this.firebaseAuthDomain,
      projectId: this.firebaseProjectId,
      storageBucket: this.firebaseStorageBucket,
      messagingSenderId: this.firebaseMessagingSenderId,
      appId: this.firebaseAppId,
      measurementId: this.firebaseMeasurementId,
    };
  }

  /**
   * Initialize and validate all configurations
   */
  static initialize(): void {
    this.log("Initializing AppConfig...");
    this.log(`Environment: ${this.isDebugMode ? "Development" : "Production"}`);
    this.log(`Base URL: ${this.baseUrl}`);
    this.log(`Application ID: ${this.applicationId}`);

    try {
      this.validateApiConfiguration();
      // Firebase validation is optional - only validate if keys are provided
      if (this.firebaseApiKey) {
        this.validateFirebaseConfiguration();
      }
    } catch (error) {
      this.log(
        `Configuration validation failed: ${error instanceof Error ? error.message : String(error)}`,
        "error"
      );
      throw error;
    }
  }
}

// Auto-initialize on import (similar to Flutter's static initialization)
if (AppConfig.isDebugMode) {
  AppConfig.log("AppConfig loaded in debug mode");
}
